<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { BtnSize, BtnVariant } from '~/components/iad/btn/types';

const props = withDefaults(
  defineProps<{
    text?: string
    href?: string
    to?: RouteLocationRaw
    tag?: string
    disabled?: boolean
    loading?: boolean
    size?: BtnSize
    variant?: BtnVariant
    icon?: boolean
  }>(),
  {
    size: `medium`,
    variant: `filled`,
  },
);

defineSlots<{
  default: () => unknown
}>();

const { linkComponent, linkBindings } = useLinksBuilder(props);

const sizeClasses: Record<BtnSize, string> = {
  fit: `iad-btn--fit gap-xs`,
  small: `iad-btn--small h-6 px-s gap-2xs text-sm`,
  medium: `iad-btn--medium h-10 px-s gap-xs`,
  large: `iad-btn--large h-12 px-m gap-xs text-lg group-[.is-prestige]:px-xl`,
} as const;

const disabledClasses: Record<BtnVariant, string> = {
  filled: `bg-neutral/70 border-transparent font-semibold`,
  outlined: `bg-neutral/70 border-transparent font-semibold`,
  link: `text-neutral-30 font-semibold border-transparent`,
} as const;
const activeClasses: Record<BtnVariant, string> = {
  filled: `bg-primary hover:bg-primary-50 text-white border-primary font-semibold group-[.is-prestige]:bg-prestige-primary-40 group-[.is-prestige]:border-prestige-primary-40`,
  outlined: `bg-white hover:text-white hover:bg-primary-50 border-primary font-semibold group-[.is-prestige]:border-prestige-primary-40 group-[.is-prestige]:hover:bg-prestige-primary-40 group-[.is-prestige]:hover:text-white group-[.is-prestige]:text-prestige-primary-40`,
  link: `text-primary font-semibold border-transparent group-[.is-prestige]:text-prestige-primary-40`,
} as const;

const classes = computed(() => {
  const variantClasses = props.disabled
    ? disabledClasses[props.variant]
    : activeClasses[props.variant];

  const _classes = [
    sizeClasses[props.size],
    variantClasses,
    'group-[.is-prestige]:uppercase group-[.is-prestige]:font-medium',
  ];
  if (props.icon) {
    _classes.push(`aspect-square !px-0`);
    _classes.push(`group-[.is-prestige]:rounded-full`);
    if (props.variant !== `filled`) {
      const iconColor = props.disabled ? `text-white` : `text-primary`;
      _classes.push(iconColor);
    };
  }
  else {
    _classes.push(`group-[.is-prestige]:rounded-none`);
  }
  if (props.disabled || props.loading) _classes.push(`pointer-events-none`);
  return _classes;
});
</script>

<template>
  <component
    :is="linkComponent"
    v-bind="linkBindings"
    class="iad-btn border rounded-full inline-flex items-center justify-center whitespace-nowrap
      group-[.is-prestige]:font-title"
    :class="classes"
  >
    <slot>
      {{ text }}
    </slot>
  </component>
</template>

<style>
/* remove scoped style to lower specificity */
:where(.iad-btn svg) {
  @apply flex-none;
}
:where(.iad-btn--small svg) {
  @apply size-4;
}
:where(.iad-btn--medium svg) {
  @apply size-5;
}
:where(.iad-btn--large svg) {
  @apply size-6;
}
</style>
